import { useState, useEffect, useCallback, useRef } from 'react';
import { devLog } from "../utils/devLog";
import { createNuveiOpenOrder } from '../api/api';
import { mapStylesToNuveiFields } from '../utils/nuveiStyleMapper';
import { formatPaymentError } from '../utils/formatPaymentError';

const NUVEI_SDK_URL =
  'https://cdn.safecharge.com/safecharge_resources/v1/websdk/safecharge.js';
const NUVEI_SDK_ID = 'nuvei-safecharge-sdk';

/**
 * Custom hook for Nuvei Web SDK / Nuvei Fields (three-part card integration).
 *
 * Valid Nuvei Fields element names: ccNumber, ccExpiration, ccCvc.
 * There is no ccHolder field — cardholder name is a plain HTML input and is
 * passed as the top-level `cardHolderName` param to createPayment().
 *
 * Lifecycle:
 *  1. Dynamically loads safecharge.js from Nuvei CDN.
 *  2. Calls POST /nuvei/open-order → { sessionToken, merchantId, merchantSiteId, env,
 *     zeroAuth, transactionType? }. The server decides whether the order is a
 *     zero-authorization; this hook mirrors that choice on createPayment().
 *  3. Initialises SafeCharge({ env, merchantId, merchantSiteId }).
 *  4. Creates sfc.fields() and attaches ccNumber, ccExpiration, ccCvc iframes.
 *  5. Exposes createPayment(cardHolderName, billingAddress) which calls
 *     safecharge.createPayment() — 3DS handled natively by Nuvei.
 *
 * Stability notes:
 *  - `formData`, `itemDetails`, `styles` are intentionally captured via refs so
 *    that a keystroke in the billing form does NOT tear down and re-mount the
 *    Nuvei iframes. Once mounted, the Nuvei iframes stay attached for the life
 *    of the component; createPayment() reads the latest values at submission
 *    time through those refs.
 *
 * @see https://docs.nuvei.com/documentation/accept-payment/web-sdk/nuvei-fields/
 */
export const useNuveiCard = ({
  sessionId,
  itemDetails,
  platformItemDetails,
  selectedAddons,
  formData,
  styles,
  onReady,
  onError,
}) => {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  // True when the server opened this order as a zero-authorization (add-payment-source):
  // the card is validated and vaulted without any funds being held.
  const [zeroAuth, setZeroAuth] = useState(false);

  // DOM refs for the three hosted iframe slots (empty divs owned by Nuvei)
  const cardNumberRef = useRef(null);
  const cardExpiryRef = useRef(null);
  const cardCvcRef    = useRef(null);

  // Internal SDK refs
  const sfcRef          = useRef(null);   // SafeCharge instance
  const ccNumberField   = useRef(null);   // ccNumber field — used as paymentOption
  const fieldsRef       = useRef([]);     // All mounted fields (for cleanup)
  const sessionDataRef  = useRef(null);   // { sessionToken, merchantId, merchantSiteId, env, ...fallbacks }
  const readyMapRef     = useRef({ ccNumber: false, ccExpiration: false, ccCvc: false });
  const mountedRef      = useRef(false);
  const initializedRef  = useRef(false);

  // Stable callback refs (so changes don't re-trigger initialize)
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onReadyRef.current = onReady;
    onErrorRef.current = onError;
  }, [onReady, onError]);

  // Capture latest props via refs — initialize() runs exactly once per mount
  // even when these props change (critical to avoid re-mounting Nuvei iframes
  // on every formData keystroke).
  const formDataRef            = useRef(formData);
  const itemDetailsRef         = useRef(itemDetails);
  const platformItemDetailsRef = useRef(platformItemDetails);
  const selectedAddonsRef      = useRef(selectedAddons);
  const stylesRef              = useRef(styles);
  useEffect(() => { formDataRef.current            = formData;            }, [formData]);
  useEffect(() => { itemDetailsRef.current         = itemDetails;         }, [itemDetails]);
  useEffect(() => { platformItemDetailsRef.current = platformItemDetails; }, [platformItemDetails]);
  useEffect(() => { selectedAddonsRef.current      = selectedAddons;      }, [selectedAddons]);
  useEffect(() => { stylesRef.current              = styles;              }, [styles]);

  // ── Step 1: Load safecharge.js ──────────────────────────────────
  const loadSdk = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (window.SafeCharge) {
        resolve(window.SafeCharge);
        return;
      }

      if (document.getElementById(NUVEI_SDK_ID)) {
        const poll = setInterval(() => {
          if (window.SafeCharge) {
            clearInterval(poll);
            resolve(window.SafeCharge);
          }
        }, 50);
        return;
      }

      const script = document.createElement('script');
      script.src   = NUVEI_SDK_URL;
      script.id    = NUVEI_SDK_ID;
      script.async = false;

      script.onload = () => {
        let attempts = 0;
        const check = () => {
          attempts++;
          if (window.SafeCharge) {
            resolve(window.SafeCharge);
          } else if (attempts >= 40) {
            reject(new Error('Nuvei SafeCharge SDK failed to initialize'));
          } else {
            setTimeout(check, 50);
          }
        };
        setTimeout(check, 50);
      };

      script.onerror = () => reject(new Error('Failed to load Nuvei SDK script'));
      document.head.appendChild(script);
    });
  }, []);

  // ── Clear out anything a previous mount/attach left behind in the
  //    slot divs. Nuvei's attach() silently misbehaves if the container
  //    it is given already has children (iframe or stale nodes).
  const emptySlot = (el) => {
    if (!el) return;
    try {
      while (el.firstChild) el.removeChild(el.firstChild);
    } catch (_) {
      // ignore — we'll still try to attach
    }
  };

  // ── Step 2-4: Open order, init SDK, mount fields ────────────────
  const initialize = useCallback(async () => {
    if (initializedRef.current) return;
    if (!sessionId) return;

    initializedRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const formDataSnapshot    = formDataRef.current || {};
      const itemDetailsSnapshot = itemDetailsRef.current;

      // 2. Get session credentials from Altruon backend.
      //    Also cache email/country so createPayment can fall back to them
      //    if formData is empty at submission time.
      //    We forward platformItemDetails + selectedAddons so openOrder is
      //    opened with the REAL plan amount (and not 0.00, which causes
      //    Nuvei errCode 1201 "Invalid Amount" at createPayment time).
      const sessionData = await createNuveiOpenOrder({
        sessionId,
        itemDetails:          itemDetailsSnapshot,
        platformItemDetails:  platformItemDetailsRef.current,
        selectedAddons:       selectedAddonsRef.current,
        formData:             formDataSnapshot,
      });
      sessionDataRef.current = {
        ...sessionData,
        email:     formDataSnapshot?.email                     || '',
        country:   formDataSnapshot?.billingAddress?.country   || '',
        phone:     formDataSnapshot?.phone                     || '',
        firstName: formDataSnapshot?.firstName                 || '',
        lastName:  formDataSnapshot?.lastName                  || '',
      };
      setSessionToken(sessionData.sessionToken);
      setZeroAuth(Boolean(sessionData.zeroAuth));

      // 3. Load safecharge.js from Nuvei CDN
      await loadSdk();

      // 4. Initialize SafeCharge instance.
      //    Per Nuvei three-part-fields docs, sessionToken is passed at
      //    createPayment() time — not at SafeCharge() init.
      const sfc = window.SafeCharge({
        env:            sessionData.env || 'int',
        merchantId:     sessionData.merchantId,
        merchantSiteId: sessionData.merchantSiteId,
      });
      sfcRef.current = sfc;

      // 5. Create fields instance with mapped styles
      const fieldStyles = mapStylesToNuveiFields(stylesRef.current);
      const ScFields = sfc.fields({
        fonts: [],
        locale: 'en',
        textDirection: 'ltr',
      });

      // 6. Create the three hosted card field instances
      //    Valid names per Nuvei docs: ccNumber, ccExpiration, ccCvc
      const numberField     = ScFields.create('ccNumber',     { style: fieldStyles });
      const expirationField = ScFields.create('ccExpiration', { style: fieldStyles });
      const cvcField        = ScFields.create('ccCvc',        { style: fieldStyles });

      // Store ccNumber as the paymentOption reference for createPayment()
      ccNumberField.current = numberField;
      fieldsRef.current = [
        { name: 'ccNumber',     field: numberField },
        { name: 'ccExpiration', field: expirationField },
        { name: 'ccCvc',        field: cvcField },
      ];

      // Per-field ready tracking — only report ready once all 3 iframes
      // have fired their 'ready' event. Nuvei's attach() returns before
      // the iframe contents are actually interactive.
      const markFieldReady = (name) => {
        readyMapRef.current[name] = true;
        const allReady =
          readyMapRef.current.ccNumber &&
          readyMapRef.current.ccExpiration &&
          readyMapRef.current.ccCvc;
        if (allReady && mountedRef.current) {
          setReady(true);
          onReadyRef.current?.();
        }
      };

      fieldsRef.current.forEach(({ name, field }) => {
        field.on('ready', () => {
          // eslint-disable-next-line no-console
          devLog(`[Nuvei] field ready: ${name}`);
          markFieldReady(name);
        });
        field.on('change', (evt) => {
          if (evt?.payload?.error) {
            const raw =
              evt.payload.error.code ||
              evt.payload.error.message ||
              evt.payload.error;
            onErrorRef.current?.(formatPaymentError(raw));
          }
        });
      });

      // 7. Attach fields to DOM once the slot refs are populated AND empty.
      const attachFields = (attempt = 0) => {
        const numberEl = cardNumberRef.current;
        const expiryEl = cardExpiryRef.current;
        const cvcEl    = cardCvcRef.current;

        if (numberEl && expiryEl && cvcEl) {
          // Nuvei's `.attach()` requires a clean container — otherwise the
          // iframe is not reliably interactive.
          emptySlot(numberEl);
          emptySlot(expiryEl);
          emptySlot(cvcEl);

          try {
            numberField.attach(numberEl);
            expirationField.attach(expiryEl);
            cvcField.attach(cvcEl);
            mountedRef.current = true;
          } catch (attachErr) {
            console.error('❌ Nuvei attach failed:', attachErr);
            const msg = attachErr?.message || 'Failed to attach Nuvei card fields';
            setError(msg);
            onErrorRef.current?.(msg);
            initializedRef.current = false;
          }
        } else if (attempt < 50) {
          setTimeout(() => attachFields(attempt + 1), 100);
        } else {
          const msg = 'Nuvei card field containers did not mount in the DOM';
          console.error('❌', msg);
          setError(msg);
          onErrorRef.current?.(msg);
          initializedRef.current = false;
        }
      };

      attachFields();
    } catch (err) {
      console.error('❌ Nuvei initialization error:', err);
      const msg = err?.message || 'Failed to initialize Nuvei payment fields';
      setError(msg);
      onErrorRef.current?.(msg);
      initializedRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [sessionId, loadSdk]);

  useEffect(() => {
    initialize();
    return () => {
      // Tear down the SDK on unmount so clicking away from Nuvei and back
      // rebuilds a fresh SafeCharge instance instead of leaking an orphan
      // iframe that accepts no input.
      mountedRef.current     = false;
      initializedRef.current = false;
      try {
        fieldsRef.current.forEach(({ field }) => {
          if (field?.destroy) field.destroy();
        });
      } catch (_) {
        // best-effort cleanup
      }
      fieldsRef.current      = [];
      ccNumberField.current  = null;
      sfcRef.current         = null;
      sessionDataRef.current = null;
      readyMapRef.current    = { ccNumber: false, ccExpiration: false, ccCvc: false };
    };
  }, [initialize]);

  // ── refreshSession ──────────────────────────────────────────────
  /**
   * Re-open a Nuvei order with the latest amount and swap the sessionToken
   * before calling createPayment. This is critical because the amount sent
   * at initial mount can be stale (e.g. before addons / coupons were applied)
   * and Nuvei rejects createPayment with errCode 1201 "Invalid Amount" if the
   * openOrder amount is 0.00 or otherwise invalid.
   *
   * Swapping sessionToken is safe because the field instances are bound to the
   * SafeCharge instance, not to the sessionToken itself — the token is only
   * consumed by createPayment().
   */
  const refreshSession = useCallback(async ({ amount } = {}) => {
    if (!sessionId) return null;

    // Zero-authorization orders have no amount to keep in sync, so re-opening would
    // burn a Nuvei round trip on every submit and invalidate a perfectly good token.
    if (sessionDataRef.current?.zeroAuth) {
      return sessionDataRef.current.sessionToken || null;
    }

    const formDataSnapshot = formDataRef.current || {};

    const data = await createNuveiOpenOrder({
      sessionId,
      itemDetails:         itemDetailsRef.current,
      platformItemDetails: platformItemDetailsRef.current,
      selectedAddons:      selectedAddonsRef.current,
      formData:            formDataSnapshot,
      amount,
    });

    sessionDataRef.current = {
      ...(sessionDataRef.current || {}),
      ...data,
      email:     formDataSnapshot?.email                   || sessionDataRef.current?.email     || '',
      country:   formDataSnapshot?.billingAddress?.country || sessionDataRef.current?.country   || '',
      phone:     formDataSnapshot?.phone                   || sessionDataRef.current?.phone     || '',
      firstName: formDataSnapshot?.firstName               || sessionDataRef.current?.firstName || '',
      lastName:  formDataSnapshot?.lastName                || sessionDataRef.current?.lastName  || '',
    };
    setSessionToken(data.sessionToken);
    return data.sessionToken;
  }, [sessionId]);

  // ── createPayment ───────────────────────────────────────────────
  /**
   * Calls safecharge.createPayment() — Nuvei handles 3DS natively.
   *
   * Per Nuvei docs:
   *   - cardHolderName is a top-level string (not inside paymentOption)
   *   - paymentOption must be the ccNumber field instance (scard)
   *   - billingAddress and userDetails accept the full customer profile
   *
   * @param {string} cardHolderName  - Value from the plain-HTML name input
   * @param {Object} billingAddress  - { firstName, lastName, email, country, phone, street, address2, city, state, zipCode }
   * @returns {Promise<{status, transactionId, errCode, errorDescription, result}>}
   */
  const createPayment = useCallback((cardHolderName = '', billingAddress = {}) => {
    if (!sfcRef.current)         throw new Error('Nuvei SDK not initialized');
    if (!ccNumberField.current)  throw new Error('Nuvei card fields not mounted');
    if (!sessionDataRef.current?.sessionToken) throw new Error('No Nuvei session token');

    const sd = sessionDataRef.current;

    // Resolve each field with formData fallback — omit keys with empty values
    // so Nuvei never receives "" for a mandatory field.
    const nuveiEmail     = billingAddress.email     || sd.email     || '';
    const nuveiCountry   = billingAddress.country   || sd.country   || 'US';
    const nuveiFirstName = billingAddress.firstName || sd.firstName || '';
    const nuveiLastName  = billingAddress.lastName  || sd.lastName  || '';
    const nuveiPhone     = billingAddress.phone     || sd.phone     || '';

    const billingAddressPayload = {
      country: nuveiCountry,
      ...(nuveiEmail       && { email:     nuveiEmail }),
      ...(nuveiFirstName   && { firstName: nuveiFirstName }),
      ...(nuveiLastName    && { lastName:  nuveiLastName }),
      ...(nuveiPhone       && { phone:     nuveiPhone }),
      ...(billingAddress.street   && { address:  billingAddress.street }),
      ...(billingAddress.address2 && { address2: billingAddress.address2 }),
      ...(billingAddress.city     && { city:     billingAddress.city }),
      ...(billingAddress.state    && { state:    billingAddress.state }),
      ...(billingAddress.zipCode  && { zip:      billingAddress.zipCode }),
    };

    const params = {
      sessionToken:   sd.sessionToken,
      cardHolderName: cardHolderName ||
        `${nuveiFirstName} ${nuveiLastName}`.trim() ||
        'Card Holder',
      paymentOption:  ccNumberField.current,
      billingAddress: billingAddressPayload,
      isRebilling:    '0', // stores card token for backend MIT (subscription) cycles
      // Nuvei requires createPayment() to mirror the transactionType/amount the order was
      // opened with. For a zero-authorization the card is validated and vaulted without
      // any funds being held. Both values come from the server, which owns the decision.
      ...(sd.zeroAuth && {
        transactionType: sd.transactionType || 'Auth',
        amount:          sd.amount || '0',
      }),
    };

    devLog('Nuvei createPayment params:', JSON.stringify(params, (k, v) =>
      k === 'paymentOption' ? '[NuveiField]' : v, 2
    ));

    // Nuvei SDK uses a callback pattern. Wrap it in a Promise so callers
    // can use await. If the SDK also returns a native Promise (newer builds),
    // we honour whichever resolves first.
    return new Promise((resolve, reject) => {
      try {
        const maybePromise = sfcRef.current.createPayment(params, (res) => {
          devLog('Nuvei createPayment callback result:', res);
          resolve(res);
        });

        if (maybePromise && typeof maybePromise.then === 'function') {
          maybePromise
            .then((res) => {
              devLog('Nuvei createPayment promise result:', res);
              resolve(res);
            })
            .catch(reject);
        }
      } catch (err) {
        reject(err);
      }
    });
  }, []);

  // ── cleanup ─────────────────────────────────────────────────────
  const cleanup = useCallback(() => {
    try {
      fieldsRef.current.forEach(({ field }) => {
        if (field?.destroy) field.destroy();
      });
    } catch (_) {
      // best-effort
    }
    fieldsRef.current      = [];
    mountedRef.current     = false;
    initializedRef.current = false;
    sfcRef.current         = null;
    ccNumberField.current  = null;
    sessionDataRef.current = null;
    readyMapRef.current    = { ccNumber: false, ccExpiration: false, ccCvc: false };
    setReady(false);
    setSessionToken(null);
    setZeroAuth(false);
    setError(null);
  }, []);

  /**
   * After a declined createPayment the session token is consumed; tear down
   * fields and open a fresh Nuvei order so the customer can retry.
   */
  const reinitialize = useCallback(async () => {
    cleanup();
    await initialize();
  }, [cleanup, initialize]);

  return {
    ready,
    error,
    loading,
    sessionToken,
    zeroAuth,
    cardNumberRef,
    cardExpiryRef,
    cardCvcRef,
    createPayment,
    refreshSession,
    cleanup,
    reinitialize,
  };
};

export default useNuveiCard;
